import PropTypes from 'prop-types';
import {
	BsGeoAlt,
	BsTelephone,
	BsEnvelope,
	BsGlobe,
	BsPeople,
	BsPersonPlus,
	BsChevronRight,
	BsChevronDown,
} from 'react-icons/bs';

import EnumFilterControl from '#globalComponents/Table/filters/EnumFilterControl';
import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';
import Table from '#globalComponents/Table/Table';
import { CELL_LINK } from '#globalComponents/Table/tableStyles';
import { getInitials } from '#lib/textUtils';

import { formatEuro, formatDate } from '../../../utils/formatters';

import CompanyTagsCell from './CompanyTagsCell';
import ContactSubRows from './ContactSubRows';

// Column catalogue of the company table; the user's order and visibility are applied by the
// Table's column picker (layoutKey 'crmCompanies').
const COLUMNS = [
	{ key: 'companyName', label: 'Unternehmen', sortable: true },
	{ key: 'location', label: 'Standort', sortable: false },
	{ key: 'contact', label: 'Kontakt', sortable: false },
	{ key: 'tags', label: 'Tags', sortable: false },
	{ key: 'deals', label: 'Deals', sortable: false, align: 'right' },
	{ key: 'pipeline', label: 'Pipeline', sortable: false, align: 'right' },
	{ key: 'contactCount', label: 'Kontakte', sortable: true, align: 'right' },
	{ key: 'createdAt', label: 'Erstellt', sortable: true },
];

const EmptyValue = () => <span className="text-bgBase-800">–</span>;

const ExpandToggle = ({ isExpanded, onToggle }) => (
	<button
		type="button"
		onClick={(e) => {
			e.stopPropagation();
			onToggle();
		}}
		className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-bgBase-800 hover:text-text transition-colors"
		title={isExpanded ? 'Kontakte ausblenden' : 'Kontakte anzeigen'}
	>
		{isExpanded ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
	</button>
);

ExpandToggle.propTypes = {
	isExpanded: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};

const CompanyTable = ({
	companies,
	sortKey,
	sortDir,
	onSort,
	dealStatsByCompany,
	contactCountByCompany,
	openId,
	onRowClick,
	onAddContact,
	expandedCompanyIds,
	contactsByCompany,
	isContactSelected,
	onToggleExpand,
	onToggleContact,
	tagOptions,
	selectedTagIds,
	onFilterChange,
	...tableProps
}) => {
	const headerAccessories = {
		tags: tagOptions.length > 0 && (
			<HeaderFilter label="Tags filtern" active={selectedTagIds.length > 0}>
				<EnumFilterControl
					heading="Firmen mit einem dieser Tags"
					paramKey="tagIds"
					options={tagOptions}
					value={selectedTagIds}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
	};

	const renderers = {
		companyName: (company) => (
			<div className="flex items-center gap-2">
				<ExpandToggle
					isExpanded={expandedCompanyIds.includes(company.id)}
					onToggle={() => onToggleExpand(company.id)}
				/>
				<div className="w-8 h-8 rounded-control bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
					{getInitials(company.companyName)}
				</div>
				<div className="min-w-0">
					<div className="font-semibold text-text truncate" title={company.companyName}>
						{company.companyName}
					</div>
					{company.websiteUrl && (
						<a
							href={company.websiteUrl}
							target="_blank"
							rel="noreferrer"
							onClick={(e) => e.stopPropagation()}
							className={`text-sm truncate flex items-center gap-1 ${CELL_LINK}`}
						>
							<BsGlobe className="w-2.5 h-2.5 flex-shrink-0" />
							{company.websiteUrl.replace(/^https?:\/\//, '')}
						</a>
					)}
				</div>
			</div>
		),
		location: (company) =>
			company.addressCity ? (
				<span className="flex items-center gap-1">
					<BsGeoAlt className="w-3 h-3 text-bgBase-800 flex-shrink-0" />
					{company.addressCity}
				</span>
			) : (
				<EmptyValue />
			),
		contact: (company) => (
			<div className="flex flex-col gap-0.5">
				{company.emailAddress && (
					<span className="text-bgBase-800 flex items-center gap-1 truncate max-w-[180px]">
						<BsEnvelope className="w-2.5 h-2.5 flex-shrink-0" /> {company.emailAddress}
					</span>
				)}
				{company.phoneNumber && (
					<span className="text-bgBase-800 flex items-center gap-1">
						<BsTelephone className="w-2.5 h-2.5 flex-shrink-0" /> {company.phoneNumber}
					</span>
				)}
				{!company.emailAddress && !company.phoneNumber && <span className="text-bgBase-800">–</span>}
			</div>
		),
		tags: (company) => <CompanyTagsCell company={company} />,
		deals: (company) => {
			const stats = dealStatsByCompany.get(company.id);
			if (!stats) return <EmptyValue />;

			return (
				<div className="flex flex-col items-end gap-0.5">
					<span className="font-semibold">{stats.dealCount}</span>
					{stats.openCount > 0 && (
						<span className="text-sm text-success-700 font-medium">{stats.openCount} offen</span>
					)}
				</div>
			);
		},
		pipeline: (company) => {
			const stats = dealStatsByCompany.get(company.id);
			return stats && stats.totalValue > 0 ? (
				<span className="font-semibold">{formatEuro(stats.totalValue)}</span>
			) : (
				<EmptyValue />
			);
		},
		contactCount: (company) =>
			contactCountByCompany.has(company.id) ? (
				<span className="inline-flex items-center gap-1 font-semibold">
					<BsPeople className="w-3 h-3 text-bgBase-800" /> {contactCountByCompany.get(company.id)}
				</span>
			) : (
				<span className="text-bgBase-800">…</span>
			),
		createdAt: (company) => <span className="text-bgBase-800">{formatDate(company.createdAt)}</span>,
	};

	const columns = [
		...COLUMNS.map((col) => ({
			key: col.key,
			label: col.label,
			sortable: col.sortable,
			align: col.align,
			headerAccessory: headerAccessories[col.key] || undefined,
			render: renderers[col.key] ?? (() => null),
		})),
		{
			key: 'addContact',
			label: '',
			align: 'right',
			render: (company) => (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onAddContact(company.id);
					}}
					className="opacity-0 group-hover/row:opacity-100 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-bgBase-800 hover:text-primary-600 hover:bg-primary-100 rounded-control transition-[color,background-color,opacity] whitespace-nowrap"
					title="Kontakt hinzufügen"
				>
					<BsPersonPlus className="w-3 h-3" /> Kontakt
				</button>
			),
		},
	];

	return (
		<Table
			layoutKey="crmCompanies"
			rows={companies}
			columns={columns}
			emptyLabel="Keine Unternehmen gefunden — passen Sie Ihre Suche oder Filter an."
			highlightedId={openId}
			onRowClick={(company) => onRowClick(company.id)}
			sort={{ sortKey, sortDir, onSortChange: onSort }}
			expansion={{
				isExpanded: (id) => expandedCompanyIds.includes(id),
				renderExpanded: (company, colSpan) => (
					<ContactSubRows
						contacts={contactsByCompany.get(company.id) ?? null}
						colSpan={colSpan}
						isContactSelected={isContactSelected}
						onToggleContact={onToggleContact}
					/>
				),
			}}
			{...tableProps}
		/>
	);
};

CompanyTable.propTypes = {
	companies: PropTypes.array.isRequired,
	sortKey: PropTypes.string.isRequired,
	sortDir: PropTypes.string.isRequired,
	onSort: PropTypes.func.isRequired,
	dealStatsByCompany: PropTypes.instanceOf(Map).isRequired,
	contactCountByCompany: PropTypes.instanceOf(Map).isRequired,
	openId: PropTypes.string,
	onRowClick: PropTypes.func.isRequired,
	onAddContact: PropTypes.func.isRequired,
	expandedCompanyIds: PropTypes.array.isRequired,
	contactsByCompany: PropTypes.instanceOf(Map).isRequired,
	isContactSelected: PropTypes.func.isRequired,
	onToggleExpand: PropTypes.func.isRequired,
	onToggleContact: PropTypes.func.isRequired,
	tagOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
	selectedTagIds: PropTypes.arrayOf(PropTypes.string).isRequired,
	/** Receives `{ tagIds: string[] | undefined }`. */
	onFilterChange: PropTypes.func.isRequired,
};

export default CompanyTable;
